// Generates a full primary/accent ramp from a single hex and injects it into
// document.documentElement CSS custom properties so every component using
// --color-primary-* / --color-accent-* picks up the change instantly.

import type { ThemePreset } from "./presets";

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) return null;
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
    case g: h = ((b - r) / d + 2) / 6; break;
    default: h = ((r - g) / d + 4) / 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360; s /= 100; l /= 100;
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(hue(h + 1 / 3) * 255),
    Math.round(hue(h) * 255),
    Math.round(hue(h - 1 / 3) * 255),
  ];
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const HEX_RE = /^#[0-9a-f]{6}$/i;

export function applyPrimaryColor(hex: string): void {
  if (!HEX_RE.test(hex.trim())) return;
  const rgb = hexToRgb(hex);
  if (!rgb) return;
  const [h, s, l] = rgbToHsl(...rgb);
  const root = document.documentElement;

  // [stop, lightness%, saturation%]
  // Lighter stops use fixed lightness so they always read as tints;
  // darker stops scale relative to the base lightness.
  const steps: [number, number, number][] = [
    [50,  97,       s * 0.25],
    [100, 93,       s * 0.35],
    [200, 85,       s * 0.55],
    [300, 73,       s * 0.75],
    [400, 62,       s * 0.9],
    [500, l,        s],
    [600, l * 0.87, s],
    [700, l * 0.73, s],
    [800, l * 0.58, s * 0.95],
    [900, l * 0.40, s * 0.9],
  ];

  for (const [stop, targetL, targetS] of steps) {
    const [r, g, b] = hslToRgb(h, clamp(targetS, 0, 100), clamp(targetL, 1, 98));
    root.style.setProperty(`--color-primary-${stop}`, `${r} ${g} ${b}`);
  }
}

export function applySidebarColors(sidebarBg: string, sidebarStroke: string): void {
  const root = document.documentElement;
  // Empty string = Default theme: remove inline override so tokens.css
  // light/dark variables take over automatically.
  if (sidebarBg) root.style.setProperty("--color-sidebar-bg", sidebarBg);
  else root.style.removeProperty("--color-sidebar-bg");
  if (sidebarStroke) root.style.setProperty("--color-sidebar-stroke", sidebarStroke);
  else root.style.removeProperty("--color-sidebar-stroke");
}

export function applyTheme(preset: ThemePreset): void {
  applyPrimaryColor(preset.primary);
  applySidebarColors(preset.sidebarBg, preset.sidebarStroke);
}

export function applyAccentColor(hex: string): void {
  if (!HEX_RE.test(hex.trim())) return;
  const rgb = hexToRgb(hex);
  if (!rgb) return;
  const [h, s, l] = rgbToHsl(...rgb);
  const root = document.documentElement;

  const steps: [number, number, number][] = [
    [300, clamp(l * 1.25, 1, 96), s * 0.85],
    [500, l,                       s],
    [700, l * 0.78,                s * 0.95],
  ];
  for (const [stop, targetL, targetS] of steps) {
    const [r, g, b] = hslToRgb(h, clamp(targetS, 0, 100), clamp(targetL, 1, 98));
    root.style.setProperty(`--color-accent-${stop}`, `${r} ${g} ${b}`);
  }
}
